/**
 * Centralized HTTP client for Ezzo Admin SPA.
 *
 * Owns:
 * - session credentials + XSRF cookie/header
 * - single-flight CSRF refresh (concurrent 419s share one refresh)
 * - one-shot 419 retry
 * - error normalization
 * - request IDs
 *
 * Never stores CSRF tokens in module state / localStorage / stores.
 * Always reads the live XSRF cookie at request time.
 */

export type ErrorKind =
  | 'auth'
  | 'csrf'
  | 'forbidden'
  | 'validation'
  | 'not_found'
  | 'rate_limit'
  | 'server'
  | 'network'
  | 'unknown';

export class HttpError extends Error {
  readonly status: number;
  readonly kind: ErrorKind;
  readonly errors: Record<string, string[]>;
  readonly requestId: string;
  readonly retryable: boolean;

  constructor(init: {
    message: string;
    status: number;
    kind: ErrorKind;
    errors?: Record<string, string[]>;
    requestId: string;
    retryable?: boolean;
  }) {
    super(init.message);
    this.name = 'HttpError';
    this.status = init.status;
    this.kind = init.kind;
    this.errors = init.errors ?? {};
    this.requestId = init.requestId;
    this.retryable = init.retryable ?? false;
  }
}

export function classifyStatus(status: number): { kind: ErrorKind; retryable: boolean } {
  if (status === 401) return { kind: 'auth', retryable: false };
  if (status === 403) return { kind: 'forbidden', retryable: false };
  if (status === 419) return { kind: 'csrf', retryable: true };
  if (status === 422) return { kind: 'validation', retryable: false };
  if (status === 404) return { kind: 'not_found', retryable: false };
  if (status === 429) return { kind: 'rate_limit', retryable: true };
  if (status >= 500) return { kind: 'server', retryable: true };
  return { kind: 'unknown', retryable: false };
}

export function newRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

/** Live XSRF cookie — never cache. Laravel sets XSRF-TOKEN when the session token is issued. */
export function readXsrfToken(): string | null {
  return readCookie('XSRF-TOKEN');
}

export function buildSessionHeaders(init?: HeadersInit, hasBody = false): Headers {
  const headers = new Headers(init);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (!headers.has('X-Requested-With')) headers.set('X-Requested-With', 'XMLHttpRequest');
  if (hasBody && !headers.has('Content-Type') && !isFormDataHeaders(init)) {
    headers.set('Content-Type', 'application/json');
  }
  const xsrf = readXsrfToken();
  if (xsrf && !headers.has('X-XSRF-TOKEN')) headers.set('X-XSRF-TOKEN', xsrf);
  return headers;
}

function isFormDataHeaders(_init?: HeadersInit): boolean {
  // Caller should omit Content-Type for FormData so the browser sets the boundary.
  return false;
}

// ── Single-flight CSRF refresh ────────────────────────────────────────

let csrfRefreshInFlight: Promise<boolean> | null = null;
let csrfRefreshGeneration = 0;

/**
 * Refresh the session CSRF cookie/token.
 * Concurrent callers await the same in-flight promise (single-flight).
 * Returns true when the refresh completed successfully.
 */
export function refreshCsrfSingleFlight(): Promise<boolean> {
  if (!csrfRefreshInFlight) {
    csrfRefreshInFlight = (async () => {
      try {
        logSafe('onboarding_csrf_refresh', { generation: csrfRefreshGeneration + 1 });
        const res = await fetch('/api/csrf-cookie', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!res.ok) return false;
        csrfRefreshGeneration += 1;
        return true;
      } catch {
        return false;
      } finally {
        // Clear after microtask so late joiners still share this flight.
        queueMicrotask(() => {
          csrfRefreshInFlight = null;
        });
      }
    })();
  }
  return csrfRefreshInFlight;
}

export function csrfRefreshGenerationCount(): number {
  return csrfRefreshGeneration;
}

/** Safe structured console log — never includes cookies, CSRF tokens, or Authorization. */
export function logSafe(event: string, meta: Record<string, unknown> = {}): void {
  const safe: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    const key = k.toLowerCase();
    if (
      key.includes('csrf') ||
      key.includes('xsrf') ||
      key.includes('authorization') ||
      key.includes('cookie') ||
      key.includes('token') ||
      key.includes('secret') ||
      key.includes('password')
    ) {
      continue;
    }
    safe[k] = v;
  }
  // eslint-disable-next-line no-console
  console.info(`[ezzo] ${event}`, safe);
}

// ── Core request ─────────────────────────────────────────────────────

export type SessionRequestInit = {
  url: string;
  method?: string;
  headers?: HeadersInit;
  /** JSON-serializable object, FormData, or raw string body. */
  body?: unknown;
  signal?: AbortSignal | null;
  /** Skip the automatic 419 refresh/retry. */
  skipCsrfRecovery?: boolean;
};

function toBodyInit(body: unknown): BodyInit | undefined {
  if (body === undefined || body === null) return undefined;
  if (body instanceof FormData) return body;
  if (typeof body === 'string') return body;
  if (body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return body as BodyInit;
  }
  return JSON.stringify(body);
}

async function parseJsonBody(res: Response): Promise<Record<string, unknown> | null> {
  const text = await res.text();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : Array.isArray(parsed)
        ? ({ data: parsed } as Record<string, unknown>)
        : null;
  } catch {
    return null;
  }
}

function normalizeErrors(raw: unknown): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  if (!raw || typeof raw !== 'object') return errors;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(v)) errors[k] = v.map(String);
    else if (typeof v === 'string') errors[k] = [v];
  }
  return errors;
}

export async function sessionFetch<T>(init: SessionRequestInit): Promise<T> {
  const requestId = newRequestId();
  const { url, skipCsrfRecovery, body, headers: rawHeaders, method = 'GET', signal } = init;

  const isForm = body instanceof FormData;
  const hasBody = body !== undefined && body !== null;
  const headers = buildSessionHeaders(rawHeaders, hasBody && !isForm);
  if (isForm) headers.delete('Content-Type');
  if (!headers.has('X-Request-Id')) headers.set('X-Request-Id', requestId);

  const payload = toBodyInit(body);

  const doFetch = (hdrs: Headers): Promise<Response> =>
    fetch(url, {
      method,
      body: payload,
      credentials: 'include',
      cache: 'no-store',
      signal: signal ?? undefined,
      headers: hdrs,
    });

  let res: Response;
  try {
    res = await doFetch(headers);
  } catch (e) {
    throw new HttpError({
      message: e instanceof Error ? e.message : 'Network request failed',
      status: 0,
      kind: 'network',
      requestId,
      retryable: true,
    });
  }

  // CSRF/session mismatch → single-flight refresh → retry once with live cookie.
  if (res.status === 419 && !skipCsrfRecovery) {
    logSafe('onboarding_csrf_retry', { requestId, path: url });
    const refreshed = await refreshCsrfSingleFlight();
    if (!refreshed) {
      throw new HttpError({
        message: 'Your session expired. Sign in again to continue.',
        status: 419,
        kind: 'csrf',
        requestId,
        retryable: false,
      });
    }
    // Rebuild headers so the retry uses the NEW XSRF cookie.
    const retryHeaders = buildSessionHeaders(rawHeaders, hasBody && !isForm);
    if (isForm) retryHeaders.delete('Content-Type');
    retryHeaders.set('X-Request-Id', requestId);
    try {
      res = await doFetch(retryHeaders);
    } catch (e) {
      throw new HttpError({
        message: e instanceof Error ? e.message : 'Network request failed',
        status: 0,
        kind: 'network',
        requestId,
        retryable: true,
      });
    }
    // Second 419 → stop (no infinite loop). Surface actionable auth/session error.
    if (res.status === 419) {
      logSafe('onboarding_auth_failure', { requestId, reason: 'csrf_retry_failed' });
      throw new HttpError({
        message: 'Session security token is invalid. Refresh the page or sign in again.',
        status: 419,
        kind: 'csrf',
        requestId,
        retryable: false,
      });
    }
  }

  const parsed = await parseJsonBody(res);

  if (res.status === 401) {
    logSafe('onboarding_auth_failure', { requestId, reason: 'unauthenticated' });
  }

  if (!res.ok) {
    const classified = classifyStatus(res.status);
    const message =
      (typeof parsed?.message === 'string' && parsed.message) ||
      (res.status === 419
        ? 'Session security token mismatch.'
        : res.status === 401
          ? 'Authentication required.'
          : res.status === 403
            ? 'You do not have permission to perform this action.'
            : res.status === 429
              ? 'Too many requests. Please slow down and retry.'
              : 'Request failed');
    throw new HttpError({
      message,
      status: res.status,
      kind: classified.kind,
      errors: normalizeErrors(parsed?.errors),
      requestId,
      retryable: classified.retryable,
    });
  }

  return (parsed ?? {}) as T;
}

// ── Controlled concurrency ───────────────────────────────────────────

/**
 * Run tasks with a hard concurrency cap (default 3–5 for onboarding).
 * Never fires uncontrolled Promise.all() across dozens of clients.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const cap = Math.max(1, Math.min(limit, items.length || 1));
  const results = new Array<R>(items.length);
  let cursor = 0;

  const runners = Array.from({ length: cap }, async () => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) break;
      results[index] = await worker(items[index], index);
    }
  });

  await Promise.all(runners);
  return results;
}

export async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/** Exponential backoff for 429 / transient network. */
export async function withBackoff<T>(
  fn: () => Promise<T>,
  opts: { retries?: number; baseMs?: number; maxMs?: number } = {},
): Promise<T> {
  const retries = opts.retries ?? 2;
  const baseMs = opts.baseMs ?? 400;
  const maxMs = opts.maxMs ?? 5000;
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (e) {
      const err = e instanceof HttpError ? e : null;
      const canRetry = attempt < retries && (err?.retryable || err?.kind === 'network');
      if (!canRetry) throw e;
      const delay = Math.min(maxMs, baseMs * 2 ** attempt);
      await sleep(delay + Math.random() * 100);
      attempt += 1;
    }
  }
}
